import { useState, useRef, useEffect } from 'react';
import { Play, X, Sparkles, Volume2, VolumeX } from 'lucide-react';

const BASE = 'https://perfectdigitals.co.uk/wp-content/uploads';

// Portfolio of AI-assisted videos produced by the Perfect Digitals team.
const PORTFOLIO = [
  { title: 'Zest Cold Drink', category: 'Product Ad', src: `${BASE}/2026/06/01-Zest-Cold-Drink.mp4` },
  { title: 'Leather Shoe', category: 'Product Ad', src: `${BASE}/2026/06/03-Leather-Shoe.mp4` },
  { title: 'Headphones', category: 'Product Ad', src: `${BASE}/2026/06/04-b-Headphones.mp4` },
  { title: 'House of Light', category: 'Brand', src: `${BASE}/2026/06/05-House-of-light-V2.mp4` },
  { title: 'NARS Cream', category: 'Product Ad', src: `${BASE}/2026/06/06-NARS-CREAM-V2.mp4` },
  { title: 'Curle', category: 'Product Ad', src: `${BASE}/2026/06/07-CURLE-V2.mp4` },
  { title: 'VA Drink', category: 'Product Ad', src: `${BASE}/2026/06/08-VA-DRINK.mp4` },
  { title: 'Faxe Kondi Energy Drink', category: 'Product Ad', src: `${BASE}/2026/06/09-FAXE-KONDI-ENERGY-DRINK.mp4` },
  { title: 'Haven Insurance', category: 'Brand', src: `${BASE}/2026/07/11-Haven-Insurance.mp4` },
  { title: 'Luxury Villas Property Video', category: 'Real Estate', src: `${BASE}/2026/07/14-Luxury-Villas-Property-Video.mp4` },
  { title: 'Airise Insurance', category: 'Brand', src: `${BASE}/2026/07/15-Airise-Insurance-V2.mp4` },
];

function VideoCard({ video, onOpen }) {
  const vidRef = useRef(null);

  const handleEnter = () => {
    const el = vidRef.current;
    if (el) { el.muted = true; el.play().catch(() => {}); }
  };
  const handleLeave = () => {
    const el = vidRef.current;
    if (el) { el.pause(); el.currentTime = 0; }
  };

  return (
    <button
      type="button"
      onClick={() => onOpen(video)}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      className="group relative aspect-[9/16] rounded-2xl overflow-hidden bg-slate-900 ring-1 ring-white/10 hover:ring-violet-400/60 transition-all text-left"
      data-testid={`portfolio-card-${video.title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <video
        ref={vidRef}
        src={`${video.src}#t=0.1`}
        preload="metadata"
        muted
        playsInline
        loop
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/20 group-hover:from-black/70 transition-colors" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-12 h-12 rounded-full bg-white/15 backdrop-blur-sm border border-white/30 flex items-center justify-center group-hover:scale-110 group-hover:bg-violet-500/80 transition-all">
          <Play className="w-5 h-5 text-white fill-white ml-0.5" />
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <span className="inline-block px-2 py-0.5 rounded-full bg-violet-500/80 text-white text-[10px] font-semibold tracking-wide mb-1">{video.category}</span>
        <div className="text-white text-sm font-semibold leading-tight drop-shadow">{video.title}</div>
      </div>
    </button>
  );
}

function Lightbox({ video, onClose }) {
  const vidRef = useRef(null);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4" data-testid="portfolio-lightbox">
      <button type="button" aria-label="Close" className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <span className="inline-block px-2 py-0.5 rounded-full bg-violet-500/80 text-white text-[10px] font-semibold tracking-wide">{video.category}</span>
            <h3 className="text-white text-lg font-semibold mt-1">{video.title}</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => { const el = vidRef.current; if (el) { el.muted = !el.muted; setMuted(el.muted); } }}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition"
              aria-label={muted ? 'Unmute' : 'Mute'}
              data-testid="portfolio-lightbox-mute"
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition"
              aria-label="Close"
              data-testid="portfolio-lightbox-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <video
          ref={vidRef}
          src={video.src}
          className="w-full max-h-[80vh] rounded-2xl bg-black object-contain shadow-2xl"
          controls
          autoPlay
          playsInline
        />
      </div>
    </div>
  );
}

export default function AIVideoPortfolio() {
  const [active, setActive] = useState(null);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white/70 backdrop-blur-sm p-5 sm:p-6" data-testid="ai-video-portfolio">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-md shadow-violet-500/30">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Our AI Video Portfolio</h2>
      </div>
      <p className="text-sm text-slate-500 mb-5">A selection of AI-assisted videos produced by our team. Hover to preview, click to watch.</p>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {PORTFOLIO.map((v) => (
          <VideoCard key={v.src} video={v} onOpen={setActive} />
        ))}
      </div>

      {active && <Lightbox video={active} onClose={() => setActive(null)} />}
    </div>
  );
}
